<template>
  <div
    :class="[
      'bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6',
      hero ? `border-l-4 ${c.heroBar} ${c.heroBg}` : '',
    ]"
  >
    <!-- Header: label + tinted icon chip -->
    <div class="flex items-center justify-between mb-3">
      <span class="text-sm font-medium text-neutral-500 dark:text-neutral-400">{{ label }}</span>
      <span
        v-if="icon"
        :class="['w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0', c.chip]"
      >
        <span :class="['material-icons text-[20px]', c.icon]" aria-hidden="true">{{ icon }}</span>
      </span>
    </div>

    <!-- Loading skeleton -->
    <template v-if="loading">
      <div class="h-8 bg-neutral-200 dark:bg-neutral-700 rounded w-28 animate-pulse mb-3"></div>
      <div class="h-4 bg-neutral-100 dark:bg-neutral-700 rounded w-36 animate-pulse"></div>
    </template>

    <!-- Value -->
    <template v-else>
      <!-- One figure size across the row so baselines align; only the hero figure
           carries colour — the other cards keep their hue in the icon chip and
           sparkline, so colour marks the headline metric instead of decorating all four. -->
      <div :class="['flex items-end', unitAttached ? 'gap-0.5' : 'gap-2']">
        <span
          :class="[
            'font-display font-bold tabular-nums leading-none text-3xl',
            hero ? c.num : 'text-neutral-900 dark:text-neutral-50',
          ]"
        >{{ formattedValue }}</span>
        <span
          v-if="unit"
          :class="[
            'font-medium text-neutral-500 dark:text-neutral-400 mb-0.5',
            unitAttached ? 'text-lg leading-none' : 'text-sm',
          ]"
        >{{ unit }}</span>
      </div>

      <!-- Delta pill -->
      <div v-if="showDelta" class="mt-3 flex items-center gap-1.5">
        <span
          :class="[
            'inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full',
            delta.value >= 0
              ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400',
          ]"
        >
          <span class="material-icons text-sm" aria-hidden="true">
            {{ delta.value >= 0 ? 'trending_up' : 'trending_down' }}
          </span>
          {{ deltaText }}
        </span>
        <span v-if="delta.prevLabel" class="text-xs text-neutral-400 dark:text-neutral-500">
          {{ delta.prevLabel }}
        </span>
      </div>

      <!-- Optional one-line context under the delta (e.g. a month-end projection) -->
      <p v-if="footnote" class="mt-2 text-xs text-neutral-500 dark:text-neutral-400 leading-snug">{{ footnote }}</p>

      <!-- Sparkline (hidden automatically when data is unavailable) -->
      <div v-if="sparkline && sparkline.length" class="mt-3 -mb-1">
        <Sparkline :data="sparkline" :color="c.spark" :height="36" />
      </div>
    </template>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import Sparkline from '@/components/analytics/Sparkline.vue'
import { chartColors } from '@/composables/useChartTheme'

// Static class strings (so Tailwind's scanner picks them up — dynamic `bg-${x}` would not).
const COLORS = {
  primary: {
    num: 'text-primary-600 dark:text-primary-400',
    chip: 'bg-primary-50 dark:bg-primary-900/20',
    icon: 'text-primary-600 dark:text-primary-400',
    heroBar: 'border-l-primary-500',
    heroBg: 'bg-primary-50/40 dark:bg-primary-900/10',
    spark: chartColors.primary,
  },
  emerald: {
    num: 'text-emerald-600 dark:text-emerald-400',
    chip: 'bg-emerald-50 dark:bg-emerald-900/20',
    icon: 'text-emerald-600 dark:text-emerald-400',
    heroBar: 'border-l-emerald-500',
    heroBg: 'bg-emerald-50/40 dark:bg-emerald-900/10',
    spark: chartColors.emerald,
  },
  secondary: {
    num: 'text-secondary-600 dark:text-secondary-400',
    chip: 'bg-secondary-50 dark:bg-secondary-900/20',
    icon: 'text-secondary-600 dark:text-secondary-400',
    heroBar: 'border-l-secondary-500',
    heroBg: 'bg-secondary-50/40 dark:bg-secondary-900/10',
    spark: chartColors.secondary,
  },
  amber: {
    num: 'text-amber-600 dark:text-amber-400',
    chip: 'bg-amber-50 dark:bg-amber-900/20',
    icon: 'text-amber-600 dark:text-amber-400',
    heroBar: 'border-l-amber-500',
    heroBg: 'bg-amber-50/40 dark:bg-amber-900/10',
    spark: chartColors.amber,
  },
  violet: {
    num: 'text-violet-600 dark:text-violet-400',
    chip: 'bg-violet-50 dark:bg-violet-900/20',
    icon: 'text-violet-600 dark:text-violet-400',
    heroBar: 'border-l-violet-500',
    heroBg: 'bg-violet-50/40 dark:bg-violet-900/10',
    spark: chartColors.violet,
  },
  neutral: {
    num: 'text-neutral-700 dark:text-neutral-200',
    chip: 'bg-neutral-100 dark:bg-neutral-700/40',
    icon: 'text-neutral-600 dark:text-neutral-300',
    heroBar: 'border-l-neutral-400',
    heroBg: 'bg-neutral-50 dark:bg-neutral-900/20',
    spark: '#737373',
  },
}

export default {
  name: 'KpiCard',
  components: { Sparkline },
  props: {
    label: { type: String, required: true },
    value: { type: Number, default: null }, // raw numeric; null -> "--"
    unit: { type: String, default: '' }, // small suffix, e.g. "L", "%", "/ 1,234"
    icon: { type: String, default: '' },
    color: { type: String, default: 'primary' },
    // delta: { value: Number, suffix?: '%'|'pp', prevLabel?: String }
    delta: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    hero: { type: Boolean, default: false },
    sparkline: { type: Array, default: null },
    decimals: { type: Number, default: 0 },
    countUp: { type: Boolean, default: true },
    footnote: { type: String, default: '' },
  },
  setup(props) {
    const c = computed(() => COLORS[props.color] || COLORS.primary)

    // ---- Count-up animation ----
    const displayValue = ref(props.value == null ? null : 0)
    let rafId = null

    const prefersReduced = () =>
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const animateTo = (target) => {
      if (target == null) {
        displayValue.value = null
        return
      }
      if (!props.countUp || prefersReduced() || typeof requestAnimationFrame === 'undefined') {
        displayValue.value = target
        return
      }
      if (rafId) cancelAnimationFrame(rafId)
      const from = Number(displayValue.value) || 0
      const duration = 650
      const start = performance.now()
      const tick = (now) => {
        const p = Math.min(1, (now - start) / duration)
        const eased = 1 - Math.pow(1 - p, 3) // easeOutCubic
        displayValue.value = from + (target - from) * eased
        if (p < 1) rafId = requestAnimationFrame(tick)
        else displayValue.value = target
      }
      rafId = requestAnimationFrame(tick)
    }

    watch(() => props.value, (v) => animateTo(v))
    onMounted(() => animateTo(props.value))
    onUnmounted(() => { if (rafId) cancelAnimationFrame(rafId) })

    const formattedValue = computed(() => {
      if (props.value == null || displayValue.value == null) return '--'
      return Number(displayValue.value).toLocaleString(undefined, {
        minimumFractionDigits: props.decimals,
        maximumFractionDigits: props.decimals,
      })
    })

    // "%" reads as part of the number (79.5%), not as a separate word after it.
    const unitAttached = computed(() => props.unit === '%')

    const showDelta = computed(
      () => !!props.delta && props.delta.value != null && !Number.isNaN(Number(props.delta.value))
    )

    const deltaText = computed(() => {
      if (!showDelta.value) return ''
      const suffix = props.delta.suffix || '%'
      return `${Math.abs(Number(props.delta.value)).toFixed(1)}${suffix}`
    })

    return { c, formattedValue, showDelta, deltaText, unitAttached }
  },
}
</script>
